/** Pure parts of the runtime config loader: no fetch, no DOM, so they test under node. */

// Mirrored by `local.config_keys` in terraform/config.tf — change both together.
export const RUNTIME_CONFIG_KEYS = [
    "VITE_GROUND_STATION_URI",
    "VITE_WEB_SOCKET_AUTHENTICATION_URI",
    "VITE_BASE_URL",
    "VITE_OAUTH_BASE_LINK",
    "VITE_OAUTH_CLIENT_ID",
    "VITE_OAUTH_RESPONSE_TYPE",
    "VITE_OAUTH_GRANT_TYPE",
    "VITE_OAUTH_IDENTITY_PROVIDER",
    "VITE_OAUTH_SCOPE",
    "VITE_GOOGLE_MAPS_KEY",
    "VITE_LOCALFORAGE_API_CACHE_NAME",
    "VITE_SENTRY_ENABLED",
] as const;

export type RuntimeConfigKey = (typeof RUNTIME_CONFIG_KEYS)[number];
export type RuntimeConfig = Partial<Record<RuntimeConfigKey, string>>;

/** Keep only string values of known keys; anything else is ignored. */
export function sanitizeRuntimeConfig(raw: unknown): RuntimeConfig {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
        throw new Error("config.json is not a JSON object");
    }
    const source = raw as Record<string, unknown>;
    const clean: RuntimeConfig = {};
    for (const key of RUNTIME_CONFIG_KEYS) {
        const value = source[key];
        if (typeof value === "string") clean[key] = value;
    }
    return clean;
}

/** config.json lives next to index.html, so resolve it against the base, not the site root. */
export function resolveRuntimeConfigUrl(baseUrl: string, documentBaseUri: string): string {
    const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    return new URL("config.json", new URL(base, documentBaseUri)).toString();
}

type FetchLike = (
    url: string,
    init?: { cache?: RequestCache },
) => Promise<Pick<Response, "ok" | "status" | "headers" | "json">>;

/**
 * Fetch-once loader for config.json. `fetchImpl` and `url` are injected so it tests under
 * node; runtime-config.ts is the DOM binding.
 *
 * `load()` never rejects: any failure leaves the config empty, so `get` returns the
 * fallback. A reply that is not JSON (the SPA fallback serves index.html for a missing
 * file) means "no runtime config" and is not an error. A failed request, a non-2xx status
 * or a JSON body that does not parse or is not an object is recorded in `getError()`.
 */
export function createRuntimeConfigLoader(fetchImpl: FetchLike, url: string) {
    let config: RuntimeConfig | null = null;
    let pending: Promise<void> | null = null;
    let error: Error | null = null;

    function load(): Promise<void> {
        pending ??= (async () => {
            try {
                const response = await fetchImpl(url, { cache: "no-store" });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const contentType = response.headers.get("content-type") ?? "";
                if (!contentType.toLowerCase().includes("application/json")) {
                    config = {};
                    return;
                }
                config = sanitizeRuntimeConfig(await response.json());
            } catch (e) {
                error = e instanceof Error ? e : new Error(String(e));
                config = {};
            }
        })();
        return pending;
    }

    /** Runtime value for `key`, else `fallback` (the build-time value). */
    function get(key: RuntimeConfigKey, fallback: string): string {
        return config?.[key] ?? fallback;
    }

    return { load, get, getError: (): Error | null => error };
}
